import { Cpu, Display, Key } from '@gravity-ui/icons';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, routes } from '../api/client.ts';
import { useCloud } from '../auth/AuthProvider.tsx';
import { useCloudStanding } from '../cloudPlan.ts';
import { ManifestView } from '../components/nodes.tsx';
import { Button, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, Page, PageHeader, StatusMark } from '../components/ui.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';

/** What remote control may do on the machine, in words rather than scope names. */
const SCOPE_WORDS: Record<string, string> = {
  'agents:read': 'see its agents',
  'agents:write': 'start and stop agents',
  'prompts:write': 'send prompts',
  'composer:write': 'type into the composer',
  'permissions:write': 'answer approvals',
  'teams:write': 'run teams',
  'tasks:read': 'read the task board',
  'tasks:write': 'change the task board',
  'memory:write': 'write to memory',
  'memory:read': 'read memory',
  'settings:write': 'change settings',
  'git:write': 'commit and push',
  'ui:view': 'see the screen',
  'ui:control': 'control the screen',
  'diagrams:read': 'see diagrams',
  'diagrams:write': 'change diagrams',
  'terminals:read': 'watch terminals',
  'terminals:write': 'type into terminals',
  'home:read': 'see Home',
  'shell:read': "watch the node's console",
  'shell:write': "type in the node's console",
};

function scopeWords(scopes: string[]): string {
  const words = scopes.map((s) => SCOPE_WORDS[s] ?? s);
  return words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}` : (words[0] ?? 'do nothing');
}

type Pending = Awaited<ReturnType<typeof routes.pendingCode>>;

const norm = (c: string) => c.replace(/[^a-z0-9]/gi, '').toUpperCase();
const notWaiting = (e: unknown) => (e instanceof ApiError && e.status === 404 ? 'No workbench is waiting with that code. Codes expire ten minutes after they are shown.' : (e as Error).message);

export function EnrolPage() {
  const [params] = useSearchParams();
  // Mojo Up AI Cloud: a machine joins the organisation active here (the request names it).
  const cloud = useCloud();
  const joins = cloud?.organisation;
  // The screen rides the relay on AI Cloud, which is Teams only.
  const screenOffered = useCloudStanding().teams;
  const [code, setCode] = useState(params.get('code') ?? '');
  const [pending, setPending] = useState<Pending>();
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<{ deviceId?: string; name: string; kind?: 'node'; waiting?: boolean }>();
  const [busy, setBusy] = useState(false);
  // The code on screen was looked up already. Clicking Approve takes focus out of the code box,
  // and a second lookup on that blur would clear the panel under the pointer: the click was lost.
  const [looked, setLooked] = useState<string>();

  async function lookup(c = code) {
    if (norm(c) === looked) return;
    setError(undefined);
    setPending(undefined);
    setDone(undefined);
    setLooked(undefined);
    if (norm(c).length < 8) return;
    try {
      setPending(await routes.pendingCode(c));
      setLooked(norm(c));
    } catch (e) {
      setError(notWaiting(e));
    }
  }

  // A link with ?code= (the workbench opens one) looks the code up as the page opens, and again
  // when another such link is followed while the page is open. Everything is set when the answer
  // comes, so a link followed meanwhile wins.
  const linked = params.get('code');
  useEffect(() => {
    if (!linked || norm(linked).length < 8) return;
    let live = true;
    routes.pendingCode(linked).then(
      (p) => {
        if (!live) return;
        setCode(linked);
        setDone(undefined);
        setError(undefined);
        setPending(p);
        setLooked(norm(linked));
      },
      (e: unknown) => {
        if (!live) return;
        setCode(linked);
        setPending(undefined);
        setLooked(undefined);
        setError(notWaiting(e));
      },
    );
    return () => {
      live = false;
    };
  }, [linked]);

  async function decide(approve: boolean) {
    setBusy(true);
    setError(undefined);
    try {
      if (approve) {
        const r = await routes.approveCode(code);
        setDone(r.ok ? { deviceId: r.deviceId, name: r.name, kind: r.kind } : { name: r.name, kind: 'node', waiting: true });
      } else {
        await routes.denyCode(code);
        setDone(undefined);
        setCode('');
        setLooked(undefined);
      }
      setPending(undefined);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page narrow>
      <PageHeader
        tone={VIEW_TONES.enrol!}
        icon={<Key />}
        title="Add a workbench"
        subtitle={joins ? 'In AI Workbench choose Sign in to Mojo Up AI Cloud; it shows a code to enter here.' : 'In AI Workbench choose Sign in to your organisation; it shows a code to enter here.'}
      />
      <PartBoundary key="enrol" name="Add a workbench" tone={VIEW_TONES.enrol!}>
        <ListGroup
          title="Approve a code"
          description={
            joins ? (
              <>
                Machines you approve here join <span className="font-medium text-foreground">{joins.name}</span>
                {joins.kind === 'personal' ? ', your own space' : ''}. To approve one into another organisation, switch to it at the top of the sidebar first.
              </>
            ) : (
              'A workbench or a node shows a code while it waits. Enter it to see what is asking, then approve it.'
            )
          }
        >
          <ListRow
            title="Code shown by the workbench"
            htmlFor="enrol-code"
            description="Codes expire ten minutes after they are shown."
            {...(!pending && !done
              ? {
                  control: (
                    <Button size="sm" variant="secondary" onPress={() => void lookup()}>
                      Look up
                    </Button>
                  ),
                }
              : {})}
          >
            <input
              id="enrol-code"
              className={`${inputClass} mono max-w-xs text-lg tracking-widest`}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onBlur={() => void lookup()}
              onKeyDown={(e) => e.key === 'Enter' && void lookup()}
              placeholder="XXXX-XXXX"
              autoFocus
            />
          </ListRow>
          {error ? (
            <ListBlock>
              <ErrorNote error={error} />
            </ListBlock>
          ) : null}
          {pending && pending.kind === 'node' ? (
            <ListRow
              icon={<Cpu />}
              title={pending.name}
              description={
                <>
                  <span className="block">Node{pending.tags?.length ? ` · ${pending.tags.join(', ')}` : ''}</span>
                  <span className="block">
                    {pending.canApprove
                      ? 'Approving makes it a live node of your organisation, with you as its sponsor. To trim what it offers, approve it from Nodes, Pending instead.'
                      : 'Nodes are approved by someone who may approve nodes. Passing it on puts it in front of them; it stays offline until they decide.'}
                  </span>
                </>
              }
              control={
                <>
                  {pending.canApprove ? (
                    <Button size="sm" variant="danger-soft" isDisabled={busy} onPress={() => void decide(false)}>
                      Refuse
                    </Button>
                  ) : null}
                  <Button size="sm" variant="primary" isPending={busy} onPress={() => void decide(true)}>
                    {pending.canApprove ? 'Approve' : 'Pass to an approver'}
                  </Button>
                </>
              }
            >
              {pending.manifest ? <ManifestView manifest={pending.manifest} layout="list" /> : undefined}
            </ListRow>
          ) : null}
          {pending && pending.kind !== 'node' ? (
            <ListRow
              icon={<Display />}
              title={pending.name}
              description={
                <>
                  <span className="block">
                    Workspace <span className="mono">{pending.workspaceId}</span>
                  </span>
                  <span className="block">
                    Once approved it reports its status and receives policies, and remote control may {scopeWords(pending.defaultScopes)}.
                    {!screenOffered || pending.defaultScopes.includes('ui:view') ? '' : " Seeing and controlling its screen stay off until its owner or an administrator allows them in the workbench's Settings."}
                  </span>
                </>
              }
              control={
                <>
                  <Button size="sm" variant="danger-soft" isDisabled={busy} onPress={() => void decide(false)}>
                    Deny
                  </Button>
                  <Button size="sm" variant="primary" isPending={busy} onPress={() => void decide(true)}>
                    Approve
                  </Button>
                </>
              }
            />
          ) : null}
          {done?.waiting ? <ListRow icon={<Cpu />} title={`${done.name} is waiting for an approver`} description="It shows under Nodes, Pending for anyone who may approve nodes, with a note that you passed it on." status={<StatusMark state="idle" label="Waiting" />} /> : null}
          {done && !done.waiting ? (
            <ListRow
              icon={done.kind === 'node' ? <Cpu /> : <Display />}
              title={`${done.name} is ${done.kind === 'node' ? 'approved' : 'connected'}${joins ? ` to ${joins.name}` : ''}`}
              description={done.kind === 'node' ? 'The node receives its token on its next poll and appears under Nodes.' : 'The workbench receives its token on its next poll and appears under Workbenches.'}
              status={<StatusMark state="online" label={done.kind === 'node' ? 'Approved' : 'Connected'} />}
              compactControl
              control={
                <Link to={done.kind === 'node' ? '/nodes' : `/workbenches/${encodeURIComponent(done.deviceId ?? '')}`} className="text-sm text-accent hover:underline">
                  Open it
                </Link>
              }
            />
          ) : null}
        </ListGroup>
      </PartBoundary>
    </Page>
  );
}
